<!--
  Page jetable — Questionnaire « Gestion des colisages ».
  Publique (aucune auth). Les réponses partent par e-mail (SMTP) via POST /api/qcm/colisage.
  Route : /colisage-questions  (déclarée publique dans router.js).
-->
<template>
  <div class="qcm-wrap">
    <div class="qcm-card">
      <template v-if="!sent">
        <header class="qcm-head">
          <h1>QCM agent dev</h1>
          <p>
            Vos réponses alimentent directement nos <b>agents de développement (IA)</b> : elles leur
            permettent de <b>se coordonner</b> et de construire les <b>meilleurs prompts possibles</b>,
            afin d'implémenter exactement ce dont vous avez besoin — sans deviner ni improviser.
          </p>
          <p>
            Voici {{ questions.length }} questions sur la gestion des colisages. Pour chaque point,
            nous indiquons ce qui est fait aujourd'hui — cochez ce qui convient, et précisez si besoin.
          </p>
        </header>

        <div class="qcm-id">
          <label>
            <span>Votre nom</span>
            <input v-model="respondent" type="text" placeholder="Nom / société" />
          </label>
          <label>
            <span>E-mail (facultatif)</span>
            <input v-model="email" type="email" placeholder="vous@exemple.com" />
          </label>
        </div>

        <section v-for="(q, qi) in questions" :key="qi" class="qcm-q">
          <div class="qcm-q-title"><b>{{ qi + 1 }}.</b> {{ q.title }}</div>
          <div v-if="q.example" class="qcm-q-ex">{{ q.example }}</div>
          <div class="qcm-options">
            <label
              v-for="(opt, oi) in q.options"
              :key="oi"
              class="qcm-opt"
              :class="{ active: answers[qi].choice === opt }"
            >
              <input type="radio" :name="'q' + qi" :value="opt" v-model="answers[qi].choice" />
              <span>{{ opt }}</span>
            </label>
          </div>
          <input
            v-model="answers[qi].note"
            type="text"
            class="qcm-note"
            placeholder="Précision (facultatif)…"
          />
        </section>

        <section class="qcm-q">
          <div class="qcm-q-title"><b>+</b> Autre remarque / cas particulier</div>
          <textarea v-model="comment" rows="3" class="qcm-note" placeholder="Écrivez ici…"></textarea>
        </section>

        <div v-if="error" class="qcm-err">{{ error }}</div>

        <button class="qcm-submit" :disabled="sending" @click="submit">
          {{ sending ? 'Envoi…' : 'Envoyer mes réponses' }}
        </button>
      </template>

      <div v-else class="qcm-done">
        <div class="qcm-check">✓</div>
        <h2>Merci, c'est envoyé&nbsp;!</h2>
        <p>Vos réponses ont bien été transmises. Nous revenons vers vous rapidement.</p>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/axios.js'

export default {
  name: 'ColisageQCM',
  data () {
    const questions = [
      {
        title: "Le colisage est-il une simple info, ou une contrainte de commande ?",
        example: "Ex. : un client veut 90 piquets carrés 7×7 (palette = 230). Aujourd'hui on affiche « 230 / palette » à titre indicatif.",
        options: [
          "Info seulement (on affiche X/palette, sans contrainte)",
          "Arrondir à la palette pleine + minimum 1 palette",
          "Palette partielle autorisée mais +30 €/palette (déconditionnement)",
          "Arrondi palette + groupage +500 € si moins d'un camion complet"
        ]
      },
      {
        title: "Source des colisages pour les produits RONDS (piquets ronds, poteaux poncés)",
        example: "Deux tableaux existent. Ex. : poteau poncé Ø 10-12 → le nouveau fichier Qzero dit 60/palette.",
        options: [
          "Basculer tout sur les chiffres du nouveau fichier Qzero",
          "Garder l'ancien tableau de conditionnement pour les ronds",
          "À vérifier ensemble"
        ]
      },
      {
        title: "Tranches qui se chevauchent (fendus / demi-ronds)",
        example: "Ex. : un piquet fendu de périmètre 15 cm tombe dans « 14-16 → 500/palette » ET « 15-17 → 400/palette ».",
        options: [
          "Prendre la tranche basse (ici 500)",
          "Prendre la tranche haute (ici 400)",
          "C'est la surface en cm² qui décide",
          "Autre (préciser ci-dessous)"
        ]
      },
      {
        title: "Produits vendus au m³ / m² / ml (planches, terrasses, bardages, arbre, poteaux fraisés)",
        example: "Aujourd'hui : pas de colisage palette affiché pour ces produits.",
        options: [
          "Correct — pas de colisage palette pour eux",
          "Afficher plutôt les minimums (1 m³, longueur standard 6 m, arbre 10 m…)",
          "Certains ont quand même besoin d'un colisage palette (préciser)"
        ]
      },
      {
        title: "Ganivelles — combinaison non fabriquée en rouleau 10 m",
        example: "Ex. : ganivelle 1,8 m demandée en 10 m (non produite). Aujourd'hui : indiquée « non disponible ».",
        options: [
          "Afficher « non disponible »",
          "Proposer automatiquement l'équivalent en 5 m",
          "Mettre « sur devis »"
        ]
      },
      {
        title: "Portes (5/palette) et bois de chauffage / barbecue (50 sacs/palette)",
        example: "Aujourd'hui : non inclus dans la gestion des colisages (produits particuliers).",
        options: [
          "Les intégrer dans la gestion des colisages",
          "Les laisser traités à part (hors périmètre)"
        ]
      },
      {
        title: "Posts & Rails (colisage défini par produit : lices, piquets demi-ronds)",
        example: "Ex. : lices 3 m, périmètre 25-30 → 230/palette. Aujourd'hui : stocké mais pas affiché au catalogue.",
        options: [
          "Afficher dans le catalogue comme les autres familles",
          "Laisser en interne seulement"
        ]
      },
      {
        title: "Où doit apparaître le colisage ?",
        example: "Aujourd'hui : dans le catalogue (tableau « produits / palette » sous chaque famille).",
        options: [
          "Catalogue seulement (comme aujourd'hui)",
          "Catalogue + devis/commande (nombre de palettes)",
          "Catalogue + devis + dimensions palette (1,8 × 1,2 × 1 m)"
        ]
      }
    ]
    return {
      questions,
      answers: questions.map(() => ({ choice: '', note: '' })),
      respondent: '',
      email: '',
      comment: '',
      sending: false,
      sent: false,
      error: ''
    }
  },
  methods: {
    async submit () {
      this.error = ''
      if (!this.respondent.trim()) { this.error = 'Merci d\'indiquer votre nom.'; return }
      const answered = this.answers.filter(a => a.choice).length
      if (answered === 0) { this.error = 'Merci de répondre à au moins une question.'; return }
      this.sending = true
      try {
        const payload = {
          respondent: this.respondent,
          email: this.email,
          comment: this.comment,
          answers: this.questions.map((q, i) => ({
            question: q.title,
            answer: this.answers[i].choice,
            note: this.answers[i].note
          }))
        }
        await axios.post('/qcm/colisage', payload)
        this.sent = true
      } catch (e) {
        this.error = "L'envoi a échoué. Merci de réessayer dans un instant."
      } finally {
        this.sending = false
      }
    }
  }
}
</script>

<style scoped>
.qcm-wrap {
  min-height: 100vh;
  background: #f4f6f8;
  padding: 28px 14px;
  display: flex;
  justify-content: center;
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  color: #1e293b;
}
.qcm-card {
  width: 100%;
  max-width: 720px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(15, 23, 42, 0.08);
  padding: 30px 28px 36px;
}
.qcm-head h1 { font-size: 22px; font-weight: 800; color: #1e3f29; margin: 0 0 10px; }
.qcm-head p { font-size: 14px; color: #475569; margin: 0 0 6px; line-height: 1.5; }
.qcm-id { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin: 20px 0 8px; }
.qcm-id label { display: flex; flex-direction: column; gap: 4px; font-size: 12px; color: #64748b; font-weight: 600; }
.qcm-id input { padding: 9px 11px; border: 1px solid #cbd5e1; border-radius: 8px; font-size: 14px; }
.qcm-q { border-top: 1px solid #eef2f0; padding: 18px 0 6px; }
.qcm-q-title { font-size: 15px; font-weight: 700; color: #244f31; margin-bottom: 4px; }
.qcm-q-ex { font-size: 12.5px; color: #94a3b8; font-style: italic; margin-bottom: 10px; line-height: 1.45; }
.qcm-options { display: flex; flex-direction: column; gap: 8px; margin-bottom: 10px; }
.qcm-opt {
  display: flex; align-items: flex-start; gap: 10px; padding: 10px 12px;
  border: 1px solid #e2e8f0; border-radius: 10px; cursor: pointer; font-size: 14px;
  transition: border-color .15s, background .15s;
}
.qcm-opt:hover { border-color: #3d7e4d; }
.qcm-opt.active { border-color: #3d7e4d; background: #f1f8f3; }
.qcm-opt input { margin-top: 3px; accent-color: #3d7e4d; }
.qcm-note {
  width: 100%; padding: 9px 11px; border: 1px solid #cbd5e1; border-radius: 8px;
  font-size: 13.5px; font-family: inherit; resize: vertical;
}
.qcm-err { margin: 14px 0 0; color: #b91c1c; font-size: 13px; font-weight: 600; }
.qcm-submit {
  margin-top: 22px; width: 100%; padding: 13px; border: none; border-radius: 10px;
  background: linear-gradient(90deg, #1e3f29, #3d7e4d); color: #fff; font-size: 15px;
  font-weight: 700; cursor: pointer;
}
.qcm-submit:disabled { opacity: .6; cursor: default; }
.qcm-done { text-align: center; padding: 40px 10px; }
.qcm-check {
  width: 64px; height: 64px; margin: 0 auto 18px; border-radius: 50%;
  background: #ecfdf5; color: #16a34a; font-size: 34px; line-height: 64px; font-weight: 800;
}
.qcm-done h2 { color: #1e3f29; margin: 0 0 8px; }
.qcm-done p { color: #64748b; font-size: 14px; }
@media (max-width: 560px) {
  .qcm-id { grid-template-columns: 1fr; }
  .qcm-card { padding: 22px 16px 28px; }
}
</style>
